import { lazy } from "react";
import { ArrowRightLeft, BookOpen, FileCheck2, Inbox, Link2, Printer, ShoppingCart, UserCheck } from "lucide-react";
import { Route, Routes } from "react-router";
import type { AnimalDetail } from "../../contract";
import { defineWebModule } from "../../web/module";
import type { SalesFacet } from "./contract";
import { CatalogPage, PublicCatalog } from "./web/Catalog";
import { LotPage } from "./web/LotPage";
import { NewPacketPage, PacketPage, PublicPacket, ReceivePage } from "./web/Packets";
import { SalesHome } from "./web/SalesHome";
import "./web/sales.css";

// Pages load when opened, so the app starts fast on a phone with weak signal.
const AnimalSaleSection = lazy(() => import("./web/AnimalSale").then((m) => ({ default: m.AnimalSaleSection })));
const ContactSalesSection = lazy(() => import("./web/AnimalSale").then((m) => ({ default: m.ContactSalesSection })));
const OfferingJob = lazy(() => import("./web/OfferingJob").then((m) => ({ default: m.OfferingJob })));
const PurchaseJob = lazy(() => import("./web/Purchases").then((m) => ({ default: m.PurchaseJob })));
const PurchasesHome = lazy(() => import("./web/Purchases").then((m) => ({ default: m.PurchasesHome })));
const SaleEventForm = lazy(() => import("./web/SaleEventForm").then((m) => ({ default: m.SaleEventForm })));

const facet = (a: AnimalDetail) => a.facets.sales as SalesFacet | undefined;

function SalesWorkspace() {
  return (
    <Routes>
      <Route index element={<SalesHome />} />
      <Route path="lots/:lotId" element={<LotPage />} />
      <Route path="catalogs/:catalogId" element={<CatalogPage />} />
      <Route path="public/:token" element={<PublicCatalog />} />
      <Route path="packets/new" element={<NewPacketPage />} />
      <Route path="packets/:packetId" element={<PacketPage />} />
      <Route path="receive/:token" element={<ReceivePage />} />
      <Route path="records/:token" element={<PublicPacket />} />
      <Route path="*" element={<SalesHome />} />
    </Routes>
  );
}

export default defineWebModule({
  id: "sales",
  icons: { catalog: BookOpen, print: Printer, cart: ShoppingCart, link: Link2, inbox: Inbox, packet: FileCheck2, bidder: UserCheck, transfer: ArrowRightLeft },
  events: {
    "sales.inquiry": { Form: SaleEventForm },
    "sales.offer": { Form: SaleEventForm },
    "sales.reservation": { Form: SaleEventForm },
    "sales.agreement": { Form: SaleEventForm },
    "sales.delivery": { Form: SaleEventForm },
  },
  animalSections: [{ id: "sales", label: "Sale", order: 80, show: (a) => !!facet(a), Component: AnimalSaleSection }],
  animalActions: [
    {
      id: "sales-list", label: "Put up for sale", icon: "tag", priority: 10,
      when: (a) => a.status === "active" && !facet(a)?.status,
      run: (a, navigate) => navigate(`/work/sales?list=${a.id}`),
    },
    {
      id: "sales-agreement", label: "Record the sale", icon: "handshake", eventType: "sales.agreement", priority: 40,
      when: (a) => facet(a)?.status === "listed" || facet(a)?.status === "reserved",
      subjects: (a) => [
        { kind: "animal", id: a.id, role: "animal", label: a.label },
        ...(facet(a)?.offering_id ? [{ kind: "job" as const, id: facet(a)!.offering_id!, role: "offering" }] : []),
        ...(facet(a)?.reservation ? [{ kind: "contact" as const, id: facet(a)!.reservation!.buyer_id, role: "buyer" }] : []),
      ],
      data: (a) => ({ ...(facet(a)?.lot ? { lots: [facet(a)!.lot] } : {}), price_basis: "total" }),
    },
    {
      id: "sales-delivery", label: "Record delivery", icon: "truck", eventType: "sales.delivery", priority: 45,
      when: (a) => facet(a)?.status === "sold",
      subjects: (a) => [
        { kind: "animal", id: a.id, role: "animal", label: a.label },
        ...(facet(a)?.offering_id ? [{ kind: "job" as const, id: facet(a)!.offering_id!, role: "offering" }] : []),
        ...(facet(a)?.agreement ? [{ kind: "contact" as const, id: facet(a)!.agreement!.buyer_id, role: "buyer" }] : []),
      ],
      data: (a) => (facet(a)?.lot ? { lots: [facet(a)!.lot] } : {}),
    },
  ],
  workspaces: { sales: SalesWorkspace, purchases: PurchasesHome },
  jobs: { "sales.offering": OfferingJob, "sales.purchase": PurchaseJob },
  contactSections: [{
    id: "sales", label: "Sales", order: 50,
    show: (c) => Array.isArray(c.roles) && c.roles.some((r: string) => ["buyer", "seller", "auction", "supplier"].includes(r)),
    Component: ContactSalesSection,
  }],
});

/** Read-only buyer pages, for mounting outside the signed-in shell. */
export { PublicCatalog, PublicPacket };
